import React from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import type { IStorageLocationsRepository, StorageLocations } from "@domain/repositories/IStorageLocationsRepository";
import { TYPES } from "@utils/inversifyJS/types";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";

const storageLocationsState: { cache: StorageLocations | null; inFlight: Promise<StorageLocations> | null } = {
  cache: null,
  inFlight: null,
};

/**
 * Shared so several explorers mounting at once issue a single request.
 */
async function fetchStorageLocationsCached(repository: IStorageLocationsRepository): Promise<StorageLocations> {
  if (storageLocationsState.cache) {
    return storageLocationsState.cache;
  }
  if (storageLocationsState.inFlight) {
    return storageLocationsState.inFlight;
  }

  storageLocationsState.inFlight = (async () => {
    try {
      const locations = await repository.getStorageLocationsAsync();
      storageLocationsState.cache = locations;
      return locations;
    } finally {
      storageLocationsState.inFlight = null;
    }
  })();

  return storageLocationsState.inFlight;
}

/**
Clears the cache so the next consumer re-reads the backend configuration.
*/
export function invalidateStorageLocationsCache(): void {
  storageLocationsState.cache = null;
}

/**
 * The backend-resolved storage locations, or `null` while they are still being loaded.
 *
 * The backend is the single source of truth (`ObjectStorage:Locations:*` / `RAWFILE_CONTAINER_NAME`).
 * The statically served `/config.json` is only a fallback for deployments whose API predates this endpoint.
 */
export function useStorageLocations(): StorageLocations | null {
  const repository = useInjection<IStorageLocationsRepository>(TYPES.IStorageLocationsRepository);
  const [storageLocations, setStorageLocations] = React.useState<StorageLocations | null>(storageLocationsState.cache);

  React.useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      try {
        const locations = await fetchStorageLocationsCached(repository);
        if (!isCancelled) setStorageLocations(locations);
      } catch (error) {
        console.error("Could not load storage locations, falling back to the runtime configuration", error);
      }
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [repository]);

  return storageLocations;
}

/**
Name of the raw-file location: the Azure container, or the AWS folder inside the bucket.
*/
export function useRawFilesLocationName(): string {
  const storageLocations = useStorageLocations();
  return storageLocations?.rawFiles?.displayName || runtimeConfig?.RAWFILE_CONTAINER_NAME || "";
}

/**
Name of the fasta location: the Azure container, or the AWS folder inside the bucket.
*/
export function useFastaFilesLocationName(): string {
  const storageLocations = useStorageLocations();
  return storageLocations?.fasta?.displayName || runtimeConfig?.FASTA_CONTAINER_NAME || "";
}

/**
Name of the Results location: the Azure container, or the AWS folder inside the bucket.
*/
export function useResultsLocationName(): string {
  const storageLocations = useStorageLocations();
  return storageLocations?.results?.displayName || storageLocations?.results?.containerName || "Results";
}
